import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { User, Briefcase } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/components/ui/form'
import {
	stepCreateAccountSchema,
	type StepCreateAccountValues,
} from '../schemas/step-create-account'
import { VOLUME_OPTIONS } from '../constants'
import { getStoredAcquisition } from '@/lib/utils/acquisition-tracking'
import type { StepAboutYouData, StepAboutBusinessData, StepCreateAccountData } from '../types'

interface StepCreateAccountProps {
	aboutYou: StepAboutYouData
	aboutBusiness: StepAboutBusinessData
	defaultValues: StepCreateAccountData
	onSubmit: (data: StepCreateAccountData) => void
	onGoogleSignUp: () => void
	onBack: () => void
	onEditStep: (step: 1 | 2) => void
	loading?: boolean
	error?: string
	hideGoogleSignUp?: boolean
}

const SummaryCard = ({
	icon: Icon,
	title,
	items,
	onEdit,
}: {
	icon: React.ElementType
	title: string
	items: { label: string; value: string }[]
	onEdit: () => void
}) => (
	<div className="rounded-lg border p-4">
		<div className="mb-2 flex items-center justify-between">
			<div className="flex items-center gap-2">
				<Icon className="text-primary h-4 w-4" />
				<span className="text-sm font-medium">{title}</span>
			</div>
			<button
				type="button"
				onClick={onEdit}
				className="text-primary text-xs hover:underline"
			>
				Edit
			</button>
		</div>
		<div className="space-y-1">
			{items
				.filter((i) => i.value)
				.map((item) => (
					<div key={item.label} className="flex justify-between text-sm">
						<span className="text-muted-foreground">{item.label}</span>
						<span className="text-foreground max-w-[60%] truncate text-right">
							{item.value}
						</span>
					</div>
				))}
		</div>
	</div>
)

const getVolumeLabel = (value: string): string =>
	VOLUME_OPTIONS.find((o) => o.value === value)?.label ?? value

const formatContactInfo = (data: StepAboutYouData): string => {
	const parts: string[] = []
	if (data.contactMethods.includes('whatsapp') && data.whatsappNumber) {
		parts.push(`WhatsApp: ${data.whatsappNumber}`)
	}
	if (data.contactMethods.includes('telegram') && data.telegramUsername) {
		parts.push(`Telegram: @${data.telegramUsername}`)
	}
	return parts.join(', ') || '-'
}

export function StepCreateAccount({
	aboutYou,
	aboutBusiness,
	defaultValues,
	onSubmit,
	onGoogleSignUp,
	onBack,
	onEditStep,
	loading = false,
	error,
	hideGoogleSignUp = false,
}: StepCreateAccountProps) {
	const { t } = useTranslation('auth')

	const form = useForm<StepCreateAccountValues>({
		resolver: zodResolver(stepCreateAccountSchema),
		defaultValues: {
			accountName:
				defaultValues.accountName || `${aboutYou.firstName}'s account`,
			// First touch: a `?ref=` captured at boot shows up already applied.
			referralCode: defaultValues.referralCode || getStoredAcquisition()?.ref || '',
		},
		mode: 'onTouched',
	})

	const handleFormSubmit = (values: StepCreateAccountValues) => {
		onSubmit(values as StepCreateAccountData)
	}

	const identityItems = [
		{ label: 'Name', value: `${aboutYou.firstName} ${aboutYou.lastName}` },
		{ label: 'Email', value: aboutYou.email },
		{ label: 'Contact', value: formatContactInfo(aboutYou) },
	]

	const businessItems = [
		{
			label: 'Site live',
			value: aboutBusiness.siteLive ? 'Yes' : 'No',
		},
		{ label: 'Volume', value: getVolumeLabel(aboutBusiness.monthlyVolume) },
		{
			label: 'Processing',
			value: aboutBusiness.needsProcessing ? 'Yes' : 'No',
		},
		{
			label: 'Processors',
			value: aboutBusiness.currentProcessors.join(', '),
		},
		{ label: 'Platforms', value: aboutBusiness.platforms.join(', ') },
		{ label: 'Website', value: aboutBusiness.websiteUrl },
	]

	return (
		<div className="my-3 space-y-4 sm:my-5 sm:space-y-5">
			<div className="space-y-1 text-center">
				<h2 className="text-foreground text-2xl font-bold tracking-tight">
					{t('signUpNew.step3.title')}
				</h2>
				<p className="text-muted-foreground text-sm">
					{t('signUpNew.step3.subtitle')}
				</p>
			</div>

			{/* Summary cards */}
			<div className="space-y-3">
				<SummaryCard
					icon={User}
					title={t('signUpNew.step3.identitySummary')}
					items={identityItems}
					onEdit={() => onEditStep(1)}
				/>
				<SummaryCard
					icon={Briefcase}
					title={t('signUpNew.step3.businessSummary')}
					items={businessItems}
					onEdit={() => onEditStep(2)}
				/>
			</div>

			{/* Account name form */}
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleFormSubmit)}
					className="space-y-4"
				>
					<FormField
						control={form.control}
						name="accountName"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t('signUpNew.step3.accountName')}
								</FormLabel>
								<FormControl>
									<Input
										placeholder={t('signUpNew.step3.accountNamePlaceholder')}
										disabled={loading}
										{...field}
									/>
								</FormControl>
								<FormDescription>
									{t('signUpNew.step3.accountNameHelper')}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="referralCode"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('signUpNew.step3.referralCode')}</FormLabel>
								<FormControl>
									<Input
										placeholder={t('signUpNew.step3.referralCodePlaceholder')}
										disabled={loading}
										autoCapitalize="characters"
										{...field}
										value={field.value ?? ''}
									/>
								</FormControl>
								<FormDescription>
									{t('signUpNew.step3.referralCodeHelper')}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div id="clerk-captcha" />

					<Button
						type="submit"
						className="w-full"
						disabled={loading || !form.formState.isValid}
					>
						{loading ? (
							<span className="flex items-center gap-2">
								<span className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
								{t('signUpNew.step3.creating')}
							</span>
						) : (
							t('signUpNew.step3.createButton')
						)}
					</Button>
				</form>
			</Form>

			{error && (
				<div className="bg-destructive/10 rounded-md p-3">
					<p className="text-destructive text-sm">{error}</p>
				</div>
			)}

			{!hideGoogleSignUp && (
				<>
					{/* Divider */}
					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="border-border w-full border-t" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background text-muted-foreground px-2">
								{t('signUp.orContinueWith')}
							</span>
						</div>
					</div>

					{/* Google */}
					<Button
						variant="outline"
						type="button"
						className="w-full"
						disabled={loading}
						onClick={onGoogleSignUp}
					>
						<svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
							<path
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
								fill="#4285F4"
							/>
							<path
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
								fill="#34A853"
							/>
							<path
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
								fill="#FBBC05"
							/>
							<path
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
								fill="#EA4335"
							/>
						</svg>
						{t('signUp.continueWithGoogle')}
					</Button>
				</>
			)}

			<Button
				type="button"
				variant="ghost"
				className="w-full"
				onClick={onBack}
				disabled={loading}
			>
				{t('signUpNew.back')}
			</Button>
		</div>
	)
}
